import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  Package,
  QrCode,
  ChartBar,
  ChartLineUp,
  Users,
  Shield,
  Buildings,
  Key,
  CaretLeft,
  CaretRight,
  SignOut,
  User,
  House,
  Sun,
  Moon,
  ListChecks,
  Printer,
  Gear,
  ClockCounterClockwise,
  TreeStructure,
  Translate,
  Scan,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { cn } from '../lib/utils';

const LOGO_FULL = '/images/logo-full.png';
const LOGO_ICON = '/images/logo-icon.png';

const navigation = [
  { nameKey: 'nav.dashboard', href: '/dashboard', icon: House },
  { sectionKey: 'nav.product' },
  { nameKey: 'nav.products', href: '/products', icon: Package },
  { nameKey: 'nav.workflows', href: '/workflows', icon: TreeStructure },
  { nameKey: 'nav.generateQr', href: '/generate-qr', icon: QrCode },
  { nameKey: 'nav.qrCodes', href: '/qr-codes', icon: ListChecks },
  { nameKey: 'nav.fieldScan', href: '/field-scan', icon: Scan, permission: 'qr.scan' },
  { nameKey: 'nav.reports', href: '/reports', icon: ChartBar },
  { nameKey: 'nav.qrStats', href: '/qr-stats', icon: ChartLineUp },
  { sectionKey: 'nav.admin' },
  { nameKey: 'nav.users', href: '/users', icon: Users },
  { nameKey: 'nav.roles', href: '/roles', icon: Shield },
  { nameKey: 'nav.departments', href: '/departments', icon: Buildings },
  { nameKey: 'nav.permissions', href: '/permissions', icon: Key },
  { nameKey: 'nav.reprintRequests', href: '/reprint-requests', icon: Printer },
  { nameKey: 'nav.contractPrinters', href: '/contract-printers', icon: Printer, permission: 'printers.manage' },
  { nameKey: 'nav.printJobs', href: '/print-jobs', icon: Printer, permission: 'print.jobs.view' },
  { nameKey: 'nav.auditLog', href: '/audit-log', icon: ClockCounterClockwise },
  { sectionKey: 'nav.settings' },
  { nameKey: 'nav.organization', href: '/organization', icon: Gear },
  { nameKey: 'nav.profile', href: '/profile', icon: User },
];

const LANGUAGES = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'hi', label: 'हिंदी', flag: 'HI' },
  { code: 'kn', label: 'ಕನ್ನಡ', flag: 'KN' },
];

export default function DashboardLayout({ children }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('omnichek-lang', langCode);
  };

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const canAccessNavItem = (item) => {
    if (!item.permission) return true;
    if (!user || user === false) return false;
    const role = String(user.role || '').toLowerCase();
    const perms = Array.isArray(user.permissions) ? user.permissions : [];
    return role === 'admin' || perms.includes('all') || perms.includes(item.permission);
  };

  const visibleNavigation = navigation.filter(canAccessNavItem);
  const sidebarWidth = sidebarCollapsed ? 'w-[68px]' : 'w-[232px]';
  const mainOffset = sidebarCollapsed ? 'ml-[68px]' : 'ml-[232px]';

  return (
    <div className="flex h-screen bg-background">
      <aside
        className={cn(
          'fixed left-0 top-0 z-30 flex h-full flex-col border-r border-border bg-card transition-all duration-300',
          sidebarWidth
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-border px-3">
          {!sidebarCollapsed ? (
            <Link to="/dashboard" className="flex items-center pl-1">
              <img src={LOGO_FULL} alt="OMNICHEK" className="h-8 object-contain" />
            </Link>
          ) : (
            <Link to="/dashboard" className="flex w-full items-center justify-center">
              <img src={LOGO_ICON} alt="OMNICHEK" className="h-8 w-8 object-contain" />
            </Link>
          )}
          {!sidebarCollapsed && (
            <button
              type="button"
              onClick={() => setSidebarCollapsed(true)}
              className="rounded-xl p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              data-testid="sidebar-toggle-btn"
              aria-label="Collapse sidebar"
            >
              <CaretLeft weight="bold" className="h-4 w-4" />
            </button>
          )}
        </div>

        {sidebarCollapsed && (
          <div className="p-2">
            <button
              type="button"
              onClick={() => setSidebarCollapsed(false)}
              className="flex w-full items-center justify-center rounded-xl p-2.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Expand sidebar"
            >
              <CaretRight weight="bold" className="h-4 w-4" />
            </button>
          </div>
        )}

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2.5 py-3">
          {visibleNavigation.map((item, index) => {
            if (item.sectionKey) {
              return !sidebarCollapsed ? (
                <div
                  key={index}
                  className="mb-1 mt-5 px-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground first:mt-1"
                >
                  {t(item.sectionKey)}
                </div>
              ) : (
                <div key={index} className="my-2 mx-2 h-px bg-border" />
              );
            }

            const Icon = item.icon;
            const isActive = item.href === '/workflows'
              ? location.pathname.startsWith('/workflows')
              : location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
            const label = t(item.nameKey);

            return (
              <Link
                key={item.href}
                to={item.href}
                data-testid={`nav-${item.href.replace(/\//g, '').replace(/-/g, '-')}`}
                title={sidebarCollapsed ? label : undefined}
                className={cn(
                  'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                  isActive
                    ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-400'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                {isActive && (
                  <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-brand-500" />
                )}
                <Icon
                  weight={isActive ? 'fill' : 'regular'}
                  className={cn('h-[18px] w-[18px] flex-shrink-0', isActive && 'text-brand-600 dark:text-brand-400')}
                />
                {!sidebarCollapsed && <span className="truncate">{label}</span>}
              </Link>
            );
          })}
        </nav>
      </aside>

      <div className={cn('flex min-w-0 flex-1 flex-col transition-all duration-300', mainOffset)}>
        <header className="sticky top-0 z-20 flex h-16 items-center justify-end gap-2 border-b border-border bg-card/80 px-4 backdrop-blur-md sm:px-6">
          <div className="flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-xl px-2.5 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  data-testid="language-switcher-btn"
                >
                  <Translate weight="bold" className="h-4 w-4" />
                  <span className="hidden sm:inline">{currentLang.flag}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40 rounded-xl border-border bg-card shadow-oc-md">
                {LANGUAGES.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={cn(
                      'cursor-pointer rounded-lg',
                      lang.code === i18n.language ? 'font-semibold text-brand-600' : ''
                    )}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="mr-2 w-6 text-center text-[10px] font-bold opacity-60">{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              type="button"
              onClick={toggleTheme}
              className="rounded-xl p-2.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              data-testid="theme-toggle-btn"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <Sun weight="fill" className="h-5 w-5 text-brand-400" />
              ) : (
                <Moon weight="fill" className="h-5 w-5" />
              )}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="ml-1 flex items-center gap-2 rounded-xl py-1.5 pl-1.5 pr-2.5 text-sm text-foreground transition-colors hover:bg-muted"
                  data-testid="user-menu-btn"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400">
                    <User weight="fill" className="h-4 w-4" />
                  </div>
                  <span className="hidden max-w-[160px] truncate sm:block">{user?.email || 'User'}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl border-border bg-card shadow-oc-md">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-foreground">{user?.name || 'User'}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/profile')}
                  className="cursor-pointer rounded-lg"
                  data-testid="profile-menu-btn"
                >
                  <User className="mr-2 h-4 w-4" />
                  {t('nav.profile')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/organization')}
                  className="cursor-pointer rounded-lg"
                  data-testid="org-settings-menu-btn"
                >
                  <Buildings className="mr-2 h-4 w-4" />
                  {t('nav.organization')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer rounded-lg text-red-600 focus:bg-red-50 focus:text-red-600 dark:focus:bg-red-500/10"
                  data-testid="logout-btn"
                >
                  <SignOut className="mr-2 h-4 w-4" />
                  {t('auth.signOut')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[1400px]">{children}</div>
        </main>
      </div>
    </div>
  );
}
